import { motion } from "framer-motion";
import { ArrowDown, Sparkles, BarChart3 } from "lucide-react";
import { COMPANY, STATS } from "@/lib/index";

const springGentle = { type: "spring" as const, stiffness: 300, damping: 35 };

export default function HeroSection() {
  const scrollToLeistungen = () => {
    document.querySelector("#leistungen")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden hero-grid"
    >
      {/* Background glow orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, oklch(0.60 0.18 185) 0%, transparent 70%)" }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-15"
          style={{ background: "radial-gradient(circle, oklch(0.72 0.20 55) 0%, transparent 70%)" }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, oklch(0.35 0.10 185) 0%, transparent 70%)" }}
        />
      </div>

      <div className="container mx-auto px-6 pt-24 pb-16 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springGentle, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-medium mb-8"
          >
            <Sparkles size={14} />
            Aus pixnetmedia wird KI Werbestudio + Social Pro
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springGentle, delay: 0.2 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-foreground leading-tight mb-6"
          >
            {COMPANY.tagline.split(" ").slice(0, 1).join(" ")}{" "}
            <span className="text-muted-foreground">{COMPANY.tagline.split(" ").slice(1, 2).join(" ")}</span>{" "}
            <span className="text-muted-foreground">{COMPANY.tagline.split(" ").slice(2, 3).join(" ")}</span>{" "}
            <span
              className="relative inline-block"
              style={{ color: "oklch(0.72 0.20 55)" }}
            >
              Technologie
              <motion.span
                className="absolute -bottom-1 left-0 h-[3px] rounded-full"
                style={{ background: "oklch(0.72 0.20 55)" }}
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ delay: 0.8, duration: 0.6 }}
              />
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springGentle, delay: 0.35 }}
            className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-4 leading-relaxed"
          >
            {COMPANY.subTagline}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springGentle, delay: 0.45 }}
            className="text-base text-muted-foreground/70 max-w-2xl mx-auto mb-12"
          >
            {COMPANY.experience} Jahre europaweite Erfahrung · Professionelle Betreuung für Unternehmen & Privathaushalte
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springGentle, delay: 0.55 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
          >
            <button
              onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
              className="group px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-base hover:bg-accent transition-all hover:scale-105 glow-orange flex items-center justify-center gap-2"
            >
              Jetzt Kontakt aufnehmen
              <ArrowDown size={16} className="group-hover:translate-y-1 transition-transform" />
            </button>
            <button
              onClick={() => document.querySelector("#social-media")?.scrollIntoView({ behavior: "smooth" })}
              className="group px-8 py-4 bg-secondary border border-border text-foreground rounded-xl font-semibold text-base hover:border-primary/50 hover:bg-muted transition-all flex items-center justify-center gap-2"
            >
              <BarChart3 size={16} className="text-primary" />
              Social Media entdecken
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springGentle, delay: 0.7 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto"
          >
            {STATS.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ ...springGentle, delay: 0.75 + i * 0.08 }}
                className="relative overflow-hidden rounded-xl bg-card border border-border px-4 py-5 text-center stat-shimmer"
              >
                <div
                  className="font-heading text-2xl md:text-3xl font-bold mb-1"
                  style={{ color: "oklch(0.72 0.20 55)" }}
                >
                  {stat.value}
                </div>
                <div className="text-muted-foreground text-xs leading-tight">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="flex justify-center mt-16"
        >
          <button
            onClick={scrollToLeistungen}
            className="flex flex-col items-center gap-2 text-muted-foreground/50 hover:text-primary transition-colors group"
          >
            <span className="text-xs uppercase tracking-widest">Mehr entdecken</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            >
              <ArrowDown size={18} />
            </motion.div>
          </button>
        </motion.div>
      </div>
    </section>
  );
}
